// ── Polymarket failure-signature decoder ─────────────────────────
//
// The CLOB's error strings are terse and assume you already know the
// two-address key model; surfaced verbatim they read as code bugs and
// send people debugging the wrong layer (the Jul-7 StreamRift incident:
// a config-class key problem chased as a relay regression). This module
// maps the known signatures onto what actually went wrong + the fix.
//
// Reference: docs/polymarket-auth-reference.md (§4 failure decoder).

export interface DecodedPolyError {
  /** What actually went wrong, in key-model terms. */
  title: string;
  /** The concrete next action. */
  hint: string;
}

interface Signature {
  pattern: RegExp;
  decoded: DecodedPolyError;
}

const SIGNATURES: Signature[] = [
  {
    pattern: /maker address not allowed|deposit wallet flow/i,
    decoded: {
      title: 'Polymarket rejected the maker address — key registration problem, not a code bug',
      hint:
        'The key is registered as legacy EOA (type 0) or with the wrong funder. An admin must re-register it under the signature type + funder that match its polymarket.com account (Org Settings → Add Key has the per-type guide).',
    },
  },
  {
    pattern: /trading restricted in your region|geoblock/i,
    decoded: {
      title: 'Blocked by Polymarket region enforcement — not a key problem',
      hint:
        "Polymarket blocks by the RELAY's egress IP at order time. This lane's egress is in a restricted region; the same key works from an allowed lane. Reads and cancels still function.",
    },
  },
  {
    pattern: /not enough balance|insufficient.*(balance|allowance)|balance.*allowance/i,
    decoded: {
      title: 'Funder balance or allowance insufficient',
      hint:
        'The FUNDER address must hold the collateral (USDC; pUSD for deposit wallets). Deposit-wallet keys also cache a stale allowance after every deposit — click "Allowance" on the key row in Org Settings to refresh.',
    },
  },
  {
    pattern: /lower than the minimum/i,
    decoded: {
      title: 'Below Polymarket minimum order size',
      hint:
        'Polymarket enforces a per-market minimum (5 contracts on most sports markets — the upstream message states this market\'s exact floor).',
    },
  },
  {
    pattern: /invalid signature/i,
    decoded: {
      title: 'Signature type / funder mismatch',
      hint:
        "The signature wrapping doesn't match how the account was created (e.g. Gnosis type on a Magic account), or the order's tickSize/negRisk is stale. Verify the key type against the account vintage in Org Settings → Add Key.",
    },
  },
  {
    pattern: /signer address has to be the address of the api key/i,
    decoded: {
      title: 'Known deposit-wallet SDK bug signature',
      hint:
        'This is the upstream type-3 (POLY_1271) L1-auth bug family, not a key mistake — the vendored clob-client likely needs a version bump. Flag to engineering (docs/polymarket-auth-reference.md §6).',
    },
  },
];

/** Decode a raw Polymarket/relay error message into key-model terms.
 *  Returns null for unrecognized messages so callers can fall back to
 *  the raw string. */
export function decodePolyError(raw: string): DecodedPolyError | null {
  for (const { pattern, decoded } of SIGNATURES) {
    if (pattern.test(raw)) return decoded;
  }
  return null;
}

/** One-string convenience for existing `setResult({msg})`-style surfaces:
 *  enriched "title — hint" when the signature is known, raw otherwise.
 *  The raw upstream text rides along in parentheses so nothing is lost
 *  for forensics/screenshots. */
export function explainPolyError(raw: string): string {
  const decoded = decodePolyError(raw);
  if (!decoded) return raw;
  return `${decoded.title}. ${decoded.hint} (Upstream: ${raw})`;
}
